import { useQuery } from '@tanstack/react-query';
import { ApiQueryPromise } from '@/types';
import { getResponseJwtHeader } from '@/utils/web';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import queryKeys from '@/constants/query-keys';
import { useSiteStore } from '@/store/site';
import { getAuthToken, useAuthActions } from '@/store/auth';
import { useEffect } from 'react';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { useGetSiteConfigs } from './use-site-configs';
import { useApiBackendInfo } from './use-api-backend-info';

export interface SaveSportSortResponse {
	TicketNo: number,
	Code: number,
	Message: string
}

export interface GetSportSortResponse {
	list: SportItem[];
	deft: string[];
}

export interface SportItem {
	Posit: number;
	ID: string;
	Name: string;
}

type SaveSportSortParams = {
	sportStr: string;
	useApiBackendMenu: boolean;
	apiBackendUrl: string;
	apiBackendToken: string;
};

const saveSportSort = async ({
	sportStr,
	useApiBackendMenu,
	apiBackendUrl,
	apiBackendToken
}: SaveSportSortParams): Promise<ApiQueryPromise<Array<SaveSportSortResponse> | SaveSportSortResponse>> => {
	if (useApiBackendMenu) {
		const body = JSON.stringify({ sportSort: sportStr });
		const response = await fetchComposer.postWithBearer(
			cloudUrlParser(`${apiBackendUrl}/Preferences/SaveSportSort`),
			{ body }
		)(apiBackendToken);

		if (!response.ok) {
			throw new Error(`${response.status}: ${response.statusText}`);
		}

		return {
			data: await response.json(),
			jwtToken: getResponseJwtHeader(response)
		};
	}

	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const body = JSON.stringify({ sportStr: sportStr });
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/Setting/SaveSportSort`), { body: body })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useSaveSportSortData = (sportStr: string) => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { url: apiBackendUrl, token: apiBackendToken, isReady: isApiBackendReady } = useApiBackendInfo();
	const useApiBackendMenu = !!SiteConfigsData?.EnableSportSortApiBackend && isApiBackendReady;
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.SAVE_SPORT_SORT],
		queryFn: () => saveSportSort({
			sportStr,
			useApiBackendMenu,
			apiBackendUrl,
			apiBackendToken
		}),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {			
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};
		updateJwt();
	}, [data?.jwtToken]);
	
	return {
		JwtToken: data?.jwtToken,
		data: data?.data.Data,
		isLoading,
		isError,
		error,		
		refetch
	};
};

type GetSportSortParams = {
	useApiBackendMenu: boolean;
	apiBackendUrl: string;
	apiBackendToken: string;
};

export const getSportSortJson = async ({
	useApiBackendMenu,
	apiBackendUrl,
	apiBackendToken
}: GetSportSortParams): Promise<ApiQueryPromise<GetSportSortResponse>> => {
	if (useApiBackendMenu) {
		const response = await fetchComposer.getWithBearer(
			cloudUrlParser(`${apiBackendUrl}/Preferences/GetSportSort`)
		)(apiBackendToken);

		if (!response.ok) {
			throw new Error(`${response.status}: ${response.statusText}`);
		}

		return {
			data: await response.json(),
			jwtToken: getResponseJwtHeader(response)
		};
	}

	const authToken = getAuthToken();
	const apiDomain = useSiteStore.getState().apiDomain;
	const response = await fetchComposer.postWithBearer(cloudUrlParser(`${apiDomain.ctcdDomain}/Setting/GetSportSortJson`))(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useGetSportSortJson = () => {
	const { SiteConfigsData } = useGetSiteConfigs();
	const { url: apiBackendUrl, token: apiBackendToken, isReady: isApiBackendReady } = useApiBackendInfo();
	const useApiBackendMenu = !!SiteConfigsData?.EnableSportSortApiBackend && isApiBackendReady;

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.GET_SPORT_SORT_JSON],
		queryFn: () => getSportSortJson({
			useApiBackendMenu,
			apiBackendUrl,
			apiBackendToken
		}),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: false
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};
		updateJwt();
	}, [data?.jwtToken]);
	return {
		JwtToken: data?.jwtToken,
		data: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};
